import { useEffect, useState } from "react";
import { Wrench, ShoppingBag, TrendingUp, Package, Clock, CheckCircle2, AlertCircle, ArrowUpRight } from "lucide-react";
import { api, API_BASE } from "@/lib/api";
import { STATUS_LABELS } from "@/lib/constants";

function imgUrl(url) {
  if (!url) return "";
  if (url.startsWith("http") || url.startsWith("data:")) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
}

export default function AdminDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/admin/analytics").then((r) => setData(r.data)); }, []);

  if (!data) return <div className="text-zinc-500">Lade Daten…</div>;

  const stats = [
    { icon: AlertCircle, label: "Neue Anfragen", value: data.repairs.new, tone: "text-blue-400 bg-blue-500/10" },
    { icon: Clock, label: "Offene Reparaturen", value: data.repairs.in_progress + data.repairs.waiting_parts, tone: "text-amber-400 bg-amber-500/10" },
    { icon: CheckCircle2, label: "Abholbereit", value: data.repairs.ready_pickup, tone: "text-emerald-400 bg-emerald-500/10" },
    { icon: TrendingUp, label: "Umsatz Gesamt", value: `€${data.revenue.total.toFixed(0)}`, tone: "text-[#FF2D40] bg-[#DC1928]/10" },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold">Übersicht</div>
          <h1 className="font-display text-3xl font-bold text-white mt-1">Dashboard</h1>
        </div>
        <div className="text-sm text-zinc-500">{new Date().toLocaleDateString("de-AT", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {stats.map((s, i) => (
          <div key={i} className="bg-[#141417] rounded-2xl border border-white/5 p-5" data-testid={`stat-${i}`}>
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${s.tone}`}>
              <s.icon className="w-5 h-5" />
            </div>
            <div className="font-display text-3xl font-bold text-white mt-4 numeric">{s.value}</div>
            <div className="text-sm text-zinc-500 mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="bg-[#141417] rounded-2xl border border-white/5 p-6 lg:col-span-2">
          <h3 className="font-display font-semibold text-white mb-5">Reparatur-Status</h3>
          <div className="space-y-3">
            {Object.entries(STATUS_LABELS).map(([key, conf]) => {
              const count = data.repairs[key] || 0;
              const pct = data.repairs.total ? (count / data.repairs.total) * 100 : 0;
              return (
                <div key={key} className="flex items-center gap-4">
                  <div className={`px-3 py-1 rounded-full text-xs font-medium border ${conf.color} w-44 text-center`}>{conf.label}</div>
                  <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                    <div className="h-full bg-[#DC1928]" style={{ width: `${pct}%` }}></div>
                  </div>
                  <div className="text-sm font-medium text-white numeric w-8 text-right">{count}</div>
                </div>
              );
            })}
          </div>

          <div className="grid grid-cols-3 mt-6 pt-6 border-t border-white/5 gap-4">
            <KPI label="Bestellungen" value={data.revenue.orders} />
            <KPI label="Ø Warenkorb" value={`€${data.revenue.avg_order.toFixed(0)}`} />
            <KPI label="Kunden" value={data.customers} />
          </div>
        </div>

        <div className="bg-[#141417] rounded-2xl border border-white/5 p-6">
          <h3 className="font-display font-semibold text-white mb-5 flex items-center gap-2">
            <Wrench className="w-4 h-4 text-[#FF2D40]" /> Top Probleme
          </h3>
          <div className="space-y-3">
            {data.top_issues.slice(0, 5).map((i, idx) => (
              <div key={idx} className="flex items-center justify-between text-sm">
                <span className="text-white font-medium">{i._id}</span>
                <span className="text-zinc-500 numeric">{i.count}x</span>
              </div>
            ))}
            {data.top_issues.length === 0 && <div className="text-sm text-zinc-500">Noch keine Daten</div>}
          </div>
        </div>

        <div className="bg-[#141417] rounded-2xl border border-white/5 p-6 lg:col-span-2">
          <h3 className="font-display font-semibold text-white mb-5 flex items-center gap-2">
            <Package className="w-4 h-4 text-[#FF2D40]" /> Top Produkte
          </h3>
          {data.top_products.length === 0 && <div className="text-sm text-zinc-500">Noch keine Verkäufe</div>}
          <div className="space-y-3">
            {data.top_products.map((p, idx) => (
              <div key={idx} className="flex items-center gap-3 p-2 rounded-xl hover:bg-white/5">
                <div className="w-10 h-10 rounded-lg bg-[#0A0A0B] overflow-hidden">
                  <img src={imgUrl(p.image)} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-white">{p.name}</div>
                  <div className="text-xs text-zinc-500">{p.qty} verkauft · €{p.revenue.toFixed(2)} Umsatz</div>
                </div>
                <ArrowUpRight className="w-4 h-4 text-zinc-500" />
              </div>
            ))}
          </div>
        </div>

        <div className="bg-[#141417] rounded-2xl border border-white/5 p-6">
          <h3 className="font-display font-semibold text-white mb-5 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[#FF2D40]" /> Tagesumsatz
          </h3>
          {data.daily_revenue.length === 0 && <div className="text-sm text-zinc-500">Keine Daten</div>}
          <div className="space-y-2">
            {data.daily_revenue.map((d, idx) => (
              <div key={idx} className="flex items-center justify-between text-sm">
                <span className="text-zinc-500">{d.date}</span>
                <span className="font-medium text-white numeric">€{d.revenue.toFixed(0)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function KPI({ label, value }) {
  return (
    <div>
      <div className="font-display text-2xl font-bold text-white numeric">{value}</div>
      <div className="text-xs text-zinc-500 mt-1">{label}</div>
    </div>
  );
}
